import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["mainImage", "thumbnail", "thumbnailContainer", "lightbox", "lightboxImage", "lightboxOverlay"]
  static values = { 
    index: { type: Number, default: 0 },
    totalImages: { type: Number, default: 0 }
  }

  static classes = ["active", "hidden"]

  connect() {
    this.currentIndex = this.indexValue
    this.maxVisibleThumbnails = 4
    this.showImage(this.currentIndex)
    this.setupKeyboardListeners()
    this.setupResizeListener()
    this.updateThumbnailSizes()
    // Wait for DOM to be fully rendered before calculating thumbnail positions
    requestAnimationFrame(() => {
      this.updateThumbnailWindow()
    })
  }

  updateThumbnailSizes() {
    if (this.hasThumbnailContainerTarget && this.thumbnailTargets.length > 0) {
      const container = this.thumbnailContainerTarget
      const parentContainer = container.parentElement
      
      if (parentContainer) {
        const parentWidth = parentContainer.getBoundingClientRect().width
        const gap = 16 // 1rem = 16px (gap-4)
        const totalGaps = (this.maxVisibleThumbnails - 1) * gap
        const thumbnailWidth = (parentWidth - totalGaps) / this.maxVisibleThumbnails
        
        // Set width for all thumbnails
        this.thumbnailTargets.forEach(thumb => {
          thumb.style.width = `${thumbnailWidth}px`
        })
      }
    }
  }

  disconnect() {
    this.removeKeyboardListeners()
    this.removeResizeListener()
  }

  setupResizeListener() {
    this.boundHandleResize = this.handleResize.bind(this)
    window.addEventListener("resize", this.boundHandleResize)
  }

  removeResizeListener() {
    if (this.boundHandleResize) {
      window.removeEventListener("resize", this.boundHandleResize)
    }
  }

  handleResize() {
    // Debounce resize events
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => {
      this.updateThumbnailSizes()
      requestAnimationFrame(() => {
        this.updateThumbnailWindow()
      })
    }, 150)
  }

  setupKeyboardListeners() {
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandleKeydown)
  }

  removeKeyboardListeners() {
    if (this.boundHandleKeydown) {
      document.removeEventListener("keydown", this.boundHandleKeydown)
    }
  }

  handleKeydown(event) {
    if (this.isLightboxOpen()) {
      if (event.key === "Escape") {
        this.closeLightbox()
      } else if (event.key === "ArrowLeft") {
        event.preventDefault()
        this.lightboxPrev()
      } else if (event.key === "ArrowRight") {
        event.preventDefault()
        this.lightboxNext()
      }
    }
  }

  next(event) {
    if (event) event.stopPropagation()
    this.currentIndex = (this.currentIndex + 1) % this.totalImagesValue
    this.showImage(this.currentIndex)
    requestAnimationFrame(() => {
      this.updateThumbnailWindow()
    })
  }

  prev(event) {
    if (event) event.stopPropagation()
    this.currentIndex = (this.currentIndex - 1 + this.totalImagesValue) % this.totalImagesValue
    this.showImage(this.currentIndex)
    requestAnimationFrame(() => {
      this.updateThumbnailWindow()
    })
  }

  select(event) {
    event.stopPropagation()
    const index = parseInt(event.currentTarget.dataset.index)
    this.currentIndex = index
    this.showImage(index)
    requestAnimationFrame(() => {
      this.updateThumbnailWindow()
    })
  }

  showImage(index) {
    // Update main image
    this.mainImageTargets.forEach((img, i) => {
      if (i === index) {
        img.classList.remove("hidden")
        img.classList.add("block")
      } else {
        img.classList.add("hidden")
        img.classList.remove("block")
      }
    })

    // Update thumbnails active state
    this.thumbnailTargets.forEach((thumb, i) => {
      if (i === index) {
        thumb.classList.add("ring-4", "ring-primary")
        thumb.classList.remove("opacity-60")
      } else {
        thumb.classList.remove("ring-4", "ring-primary")
        thumb.classList.add("opacity-60")
      }
    })
  }

  updateThumbnailWindow() {
    const totalThumbnails = this.thumbnailTargets.length
    
    // Update thumbnail sizes first
    this.updateThumbnailSizes()
    
    if (totalThumbnails <= this.maxVisibleThumbnails) {
      // All thumbnails fit, show all - no transform needed
      this.thumbStart = 0
      this.thumbEnd = totalThumbnails - 1
      if (this.hasThumbnailContainerTarget) {
        this.thumbnailContainerTarget.style.transform = "translateX(0)"
      }
      return
    }

    // Calculate window to keep currentIndex visible
    // Try to center currentIndex in the window when possible
    let thumbStart = Math.max(0, this.currentIndex - Math.floor(this.maxVisibleThumbnails / 2))
    let thumbEnd = Math.min(totalThumbnails - 1, thumbStart + this.maxVisibleThumbnails - 1)
    
    // Adjust if we're at the end (can't show more thumbnails)
    if (thumbEnd === totalThumbnails - 1) {
      thumbStart = Math.max(0, thumbEnd - this.maxVisibleThumbnails + 1)
    }
    
    // Adjust if we're at the beginning
    if (thumbStart === 0) {
      thumbEnd = Math.min(totalThumbnails - 1, this.maxVisibleThumbnails - 1)
    }
    
    this.thumbStart = thumbStart
    this.thumbEnd = thumbEnd

    // Update container transform to scroll to the active thumbnail
    if (this.hasThumbnailContainerTarget) {
      const container = this.thumbnailContainerTarget
      const parentContainer = container.parentElement
      const activeThumb = this.thumbnailTargets[this.currentIndex]
      const firstThumb = this.thumbnailTargets[0]
      
      if (activeThumb && firstThumb && parentContainer) {
        // Get dimensions
        const parentRect = parentContainer.getBoundingClientRect()
        const activeThumbRect = activeThumb.getBoundingClientRect()
        const firstThumbRect = firstThumb.getBoundingClientRect()
        
        // Calculate the gap between thumbnails
        const gap = 16 // 1rem = 16px (gap-4)
        
        // Calculate the width of one thumbnail
        const thumbWidth = activeThumbRect.width
        
        // Calculate the center position of the parent container
        const parentCenter = parentRect.width / 2
        
        // Calculate the position of the active thumbnail relative to the first thumbnail
        // Each thumbnail is thumbWidth + gap apart (except the first one)
        const activeThumbPosition = this.currentIndex * (thumbWidth + gap)
        
        // Calculate the center position of the active thumbnail relative to the first thumbnail
        const activeThumbCenter = activeThumbPosition + (thumbWidth / 2)
        
        // Calculate how much we need to shift the container to center the active thumbnail
        // We want the active thumbnail center to align with the parent center
        const offsetNeeded = parentCenter - activeThumbCenter
        
        // Apply the transform
        container.style.transform = `translateX(${offsetNeeded}px)`
      }
    }
  }

  openLightbox(event) {
    // Don't open if clicking on navigation buttons or thumbnails
    if (event.target.closest("button") || event.target.closest("[data-gallery-target='thumbnail']")) {
      return
    }

    this.showLightboxImage(this.currentIndex)
    this.lightboxTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  closeLightbox(event) {
    if (event) event.stopPropagation()
    this.lightboxTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  lightboxNext(event) {
    event.stopPropagation()
    this.currentIndex = (this.currentIndex + 1) % this.totalImagesValue
    this.showLightboxImage(this.currentIndex)
    this.showImage(this.currentIndex)
    requestAnimationFrame(() => {
      this.updateThumbnailWindow()
    })
  }

  lightboxPrev(event) {
    event.stopPropagation()
    this.currentIndex = (this.currentIndex - 1 + this.totalImagesValue) % this.totalImagesValue
    this.showLightboxImage(this.currentIndex)
    this.showImage(this.currentIndex)
    requestAnimationFrame(() => {
      this.updateThumbnailWindow()
    })
  }

  showLightboxImage(index) {
    this.lightboxImageTargets.forEach((img, i) => {
      if (i === index) {
        img.classList.remove("hidden")
        img.classList.add("block")
      } else {
        img.classList.add("hidden")
        img.classList.remove("block")
      }
    })
  }

  isLightboxOpen() {
    return !this.lightboxTarget.classList.contains("hidden")
  }

}
